import { create } from 'zustand';
import {
  getNotifications,
  getUnreadNotificationCount,
  markNotificationsRead as apiMarkRead,
} from '../services/api';

export interface AppNotification {
  id: string;
  notification_type: string;
  title: string;
  body: string;
  data: Record<string, unknown> | null;
  load_id: string | null;
  message_id: string | null;
  sender_id: string | null;
  sender_name: string | null;
  is_read: boolean;
  created_at: string;
}

interface NotificationState {
  notifications: AppNotification[];
  unreadCount: number;
  isPanelOpen: boolean;
  isLoading: boolean;
  onNavigateToLoad: ((loadId: string) => void) | null;
  pendingLoadNavigation: string | null;

  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAllRead: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  togglePanel: () => void;
  closePanel: () => void;
  addNotification: (notification: AppNotification) => void;
  setOnNavigateToLoad: (cb: ((loadId: string) => void) | null) => void;
  navigateToLoad: (loadId: string) => void;
  consumePendingNavigation: () => string | null;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isPanelOpen: false,
  isLoading: false,
  onNavigateToLoad: null,
  pendingLoadNavigation: null,

  fetchNotifications: async () => {
    set({ isLoading: true });
    try {
      const res = await getNotifications();
      set({ notifications: res.items || [], isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const res = await getUnreadNotificationCount();
      set({ unreadCount: res.unread_count || 0 });
    } catch {
      // ignore
    }
  },

  markAllRead: async () => {
    try {
      await apiMarkRead(null);
      set((state) => ({
        unreadCount: 0,
        notifications: state.notifications.map((n) => ({ ...n, is_read: true })),
      }));
    } catch {
      // ignore
    }
  },

  markRead: async (id: string) => {
    try {
      await apiMarkRead([id]);
      set((state) => ({
        unreadCount: Math.max(0, state.unreadCount - 1),
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, is_read: true } : n
        ),
      }));
    } catch {
      // ignore
    }
  },

  togglePanel: () => {
    const willOpen = !get().isPanelOpen;
    set({ isPanelOpen: willOpen });
    if (willOpen) {
      get().fetchNotifications();
    }
  },

  closePanel: () => set({ isPanelOpen: false }),

  addNotification: (notification: AppNotification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications].slice(0, 100),
      unreadCount: state.unreadCount + 1,
    }));
  },

  setOnNavigateToLoad: (cb) => set({ onNavigateToLoad: cb }),

  navigateToLoad: (loadId: string) => {
    const { onNavigateToLoad } = get();
    if (onNavigateToLoad) {
      // Dashboard is mounted — navigate directly
      onNavigateToLoad(loadId);
      set({ isPanelOpen: false });
    } else {
      // Dashboard not mounted — store for pickup on mount
      set({ pendingLoadNavigation: loadId, isPanelOpen: false });
    }
  },

  consumePendingNavigation: () => {
    const loadId = get().pendingLoadNavigation;
    if (loadId) set({ pendingLoadNavigation: null });
    return loadId;
  },
}));
